export interface InventoryField {
  id: number | string;
  label: string;
  fieldKey: string;
  type: string;
  required: boolean;
  showInList: boolean;
  showInForm: boolean;
  active: boolean;
  options?: string[];
  order: number;
  placeholder?: string;
}

export const defaultInventoryFields: InventoryField[] = [
  { id: "aaaaaaaa-1111-4aaa-aaaa-111111111111", label: "Unit Number", fieldKey: "unitNo", type: "Text Input", required: true, showInList: true, showInForm: true, active: true, order: 1, placeholder: "e.g. A-101" },
  { id: "aaaaaaaa-2222-4aaa-aaaa-222222222222", label: "Type", fieldKey: "type", type: "Dropdown", required: true, showInList: true, showInForm: true, active: true, options: ["2BHK", "3BHK", "Villa", "Plot", "Commercial"], order: 2 },
  { id: "aaaaaaaa-3333-4aaa-aaaa-333333333333", label: "Floor", fieldKey: "floor", type: "Number Input", required: false, showInList: false, showInForm: true, active: true, order: 3, placeholder: "e.g. 1" },
  { id: "aaaaaaaa-4444-4aaa-aaaa-444444444444", label: "Area (sqft)", fieldKey: "area", type: "Number Input", required: false, showInList: true, showInForm: true, active: true, order: 4, placeholder: "e.g. 950" },
  { id: "aaaaaaaa-5555-4aaa-aaaa-555555555555", label: "Price", fieldKey: "price", type: "Currency", required: true, showInList: true, showInForm: true, active: true, order: 5, placeholder: "e.g. 1.8 Cr" },
  { id: "aaaaaaaa-6666-4aaa-aaaa-666666666666", label: "Status", fieldKey: "status", type: "Dropdown", required: true, showInList: true, showInForm: true, active: true, options: ["Available", "Booked", "Sold", "On Hold"], order: 6 },
  { id: "aaaaaaaa-7777-4aaa-aaaa-777777777777", label: "Buyer Name", fieldKey: "buyer", type: "Text Input", required: false, showInList: false, showInForm: true, active: true, order: 7, placeholder: "e.g. Vikram Mehta" },
];

export const inventoryFieldTypeOptions = [
  "Text Input",
  "Number Input",
  "Currency",
  "Dropdown",
  "Date",
  "Toggle",
  "Textarea",
  "Phone",
  "Email",
];

export interface InventoryIndustryExample {
  id: string;
  name: string;
  emoji: string;
  fields: { label: string; type: string }[];
}

export const inventoryIndustryExamples: InventoryIndustryExample[] = [
  {
    id: "real-estate",
    name: "Real Estate — Units",
    emoji: "🏢",
    fields: [
      { label: "Unit Number", type: "Text Input" },
      { label: "Type", type: "Dropdown" },
      { label: "Floor", type: "Number Input" },
      { label: "Area (sqft)", type: "Number Input" },
      { label: "Price", type: "Currency" },
      { label: "Status", type: "Dropdown" },
      { label: "Buyer Name", type: "Text Input" },
    ],
  },
  {
    id: "manufacturing",
    name: "Manufacturing — Products",
    emoji: "📦",
    fields: [
      { label: "SKU", type: "Text Input" },
      { label: "Product Name", type: "Text Input" },
      { label: "Category", type: "Text Input" },
      { label: "Stock Qty", type: "Number Input" },
      { label: "Unit Price", type: "Currency" },
      { label: "MOQ", type: "Number Input" },
      { label: "Status", type: "Dropdown" },
    ],
  },
  {
    id: "education",
    name: "Education — Batches",
    emoji: "🎓",
    fields: [
      { label: "Batch No", type: "Text Input" },
      { label: "Course Name", type: "Text Input" },
      { label: "Total Seats", type: "Number Input" },
      { label: "Enrolled", type: "Number Input" },
      { label: "Fee", type: "Currency" },
      { label: "Start Date", type: "Date" },
      { label: "Status", type: "Dropdown" },
    ],
  },
  {
    id: "healthcare",
    name: "Healthcare — Packages",
    emoji: "🏥",
    fields: [
      { label: "Package Name", type: "Text Input" },
      { label: "Duration", type: "Text Input" },
      { label: "Price", type: "Currency" },
      { label: "Slots", type: "Number Input" },
      { label: "Validity", type: "Text Input" },
      { label: "Status", type: "Dropdown" },
    ],
  },
  {
    id: "saas",
    name: "SaaS — Plans",
    emoji: "💻",
    fields: [
      { label: "Plan Name", type: "Text Input" },
      { label: "Price/Month", type: "Currency" },
      { label: "Price/Year", type: "Currency" },
      { label: "Max Users", type: "Number Input" },
      { label: "Trial Days", type: "Number Input" },
      { label: "Status", type: "Dropdown" },
    ],
  },
  {
    id: "retail",
    name: "Retail — Products",
    emoji: "🛒",
    fields: [
      { label: "Product Name", type: "Text Input" },
      { label: "SKU", type: "Text Input" },
      { label: "Category", type: "Text Input" },
      { label: "Stock", type: "Number Input" },
      { label: "Selling Price", type: "Currency" },
      { label: "Status", type: "Dropdown" },
    ],
  },
];

export function getStatusBadgeColor(status: string, options?: string[]): string {
  if (!options || options.length === 0) return "bg-gray-100 text-gray-600";
  const idx = options.indexOf(status);
  if (idx === 0) return "bg-green-50 text-green-700";
  if (idx === 1) return "bg-amber-50 text-amber-700";
  if (idx === 2) return "bg-red-50 text-red-700";
  if (idx === 3) return "bg-gray-100 text-gray-600";
  return "bg-gray-100 text-gray-600";
}

export function labelToKey(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, "")
    .replace(/\s+/g, "_")
    .replace(/_{2,}/g, "_")
    .replace(/^_|_$/g, "");
}